/**
 * Where on a site the widget appears. The business chooses a mode and a
 * list of path rules in the dashboard; the widget decides per page, here,
 * so a single-page app is handled as it navigates rather than only on load.
 */
export type PathMode = "all" | "include" | "exclude";

/**
 * Whether a path rule matches a pathname. `*` matches any run of characters,
 * including slashes, so `/blog/*` covers every post however deep. Matching
 * is case-insensitive and ignores a trailing slash on either side, since
 * `/pricing` and `/pricing/` are the same page to a visitor.
 */
export function matchesPath(pathname: string, rule: string): boolean {
  const norm = (s: string) => {
    const trimmed = s.trim().toLowerCase();
    const noSlash = trimmed.length > 1 ? trimmed.replace(/\/+$/, "") : trimmed;
    return noSlash || "/";
  };
  const path = norm(pathname);
  const pattern = norm(rule);
  if (!pattern.includes("*")) return path === pattern;
  const source = pattern
    .split("*")
    .map((part) => part.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"))
    .join(".*");
  return new RegExp(`^${source}$`).test(path);
}

export function isShownOn(pathname: string, mode: PathMode, rules: readonly string[]): boolean {
  if (mode === "all" || rules.length === 0) return true;
  const hit = rules.some((rule) => matchesPath(pathname, rule));
  return mode === "include" ? hit : !hit;
}

/**
 * Calls back whenever the path changes: back/forward, and the pushState /
 * replaceState calls every SPA router makes. Returns a function that stops
 * watching. The history patch is shared with any other listener via a
 * single custom event, so patching twice is harmless.
 */
export function watchPath(onChange: (pathname: string) => void): () => void {
  let current = window.location.pathname;
  const check = () => {
    const next = window.location.pathname;
    if (next !== current) {
      current = next;
      onChange(next);
    }
  };

  const w = window as unknown as { __minayaHistoryPatched?: boolean };
  if (!w.__minayaHistoryPatched) {
    w.__minayaHistoryPatched = true;
    for (const method of ["pushState", "replaceState"] as const) {
      const original = history[method];
      history[method] = function (this: History, ...args: Parameters<History["pushState"]>) {
        const result = original.apply(this, args);
        window.dispatchEvent(new Event("minaya:navigate"));
        return result;
      };
    }
  }

  window.addEventListener("popstate", check);
  window.addEventListener("hashchange", check);
  window.addEventListener("minaya:navigate", check);
  return () => {
    window.removeEventListener("popstate", check);
    window.removeEventListener("hashchange", check);
    window.removeEventListener("minaya:navigate", check);
  };
}
